import React, { useEffect, useState } from "react";
import { FunctionComponent } from "react";
import ColumnGroupTable from "@murphy-frontend/web-core/components/ColumnGroupTable";
import { ColumnGroup, ColumnGroupColumnCell, ColumnGroupRowCell } from "@murphy-frontend/web-core/components/ColumnGroupTable/Models";
import { createColumnGroup, sortByKey } from "@murphy-frontend/web-core/components/ColumnGroupTable/TableUtils";
import { ContactViewModel, OptionSetViewModel } from '../../../common/api/Contacts/ContactsApi';
import TableCellActionDropDownButton from "@murphy-frontend/web-core/components/ColumnGroupTable/TableCellActionDropDownButton";
import { IconContext } from "react-icons";
import { RiDeleteBin7Fill, RiPencilFill } from "react-icons/ri";

interface IContactListPaginated {
    contacts: ContactViewModel[],
    optionSets?: OptionSetViewModel[],
    translations: Record<string, string>,
    onClickEdit?: (id: number) => void,
    onClickDelete?: (id: number) => void
}

export const ContactListPaginated: FunctionComponent<IContactListPaginated> = ({ contacts, optionSets = [], translations, onClickEdit, onClickDelete }) => {
    const [columnGroups, setColumnGroups] = useState<ColumnGroup[]>([]);
    const [currentSortingColumn, setCurrentSortingColumn] = useState<string>('name');
    const [sortedAscending, setSortedAscending] = useState<boolean>(true);
    const [pageNum, setPageNum] = useState<number>(1)
    const [visibleDropDownId, setVisibleDropDownId] = useState(null)

    const sortByColumn = (columnKey: string) => {
        setCurrentSortingColumn(columnKey);
        const newSortingOrder = !sortedAscending;
        setSortedAscending(newSortingOrder);
    };

    const navigateHandler = (num: number) => {
        setPageNum(num)
    }

    const createNameColumnGroup = (contacts: ContactViewModel[]) => {
        const nameColumn: ColumnGroupColumnCell[] = [
            {
                columnKey: 'name',
                displayName: translations.nam || 'Name',
                isSortable: true,
                size: 'column-flex',
                onClickColumn: sortByColumn,
            },
        ];

        const nameRows: ColumnGroupRowCell[][] = contacts.map((contactRow) => ([{
            columnKey: 'name',
            id: `name-${contactRow.id}`,
            rowId: contactRow.id,
            title: contactRow.name,
            displayValue: (
                <div className="vertical-aligner space-between-flex">
                    <span>
                        {contactRow.name}
                    </span>
                </div>
            )
        }]));

        const nameColumnGroup = createColumnGroup('', nameColumn, nameRows);

        return nameColumnGroup;
    };

    const createEmailAddressColumnGroup = (contacts: ContactViewModel[]) => {
        const emailAddressColumn: ColumnGroupColumnCell[] = [
            {
                columnKey: 'email',
                displayName: translations.email || 'Email',
                isSortable: true,
                size: 'column-flex',
                onClickColumn: sortByColumn,
            },
        ];

        const emailAddressRows: ColumnGroupRowCell[][] = contacts.map((contactRow) => ([
            {
                columnKey: 'email',
                id: `email-${contactRow.id}`,
                rowId: contactRow.id,
                title: contactRow.email,
                displayValue: contactRow.email || '-'
            },
        ]));

        const emailAddressColumnGroup = createColumnGroup('', emailAddressColumn, emailAddressRows);

        return emailAddressColumnGroup;
    };

    const createPhoneNumberColumnGroup = (contacts: ContactViewModel[]) => {
        const phoneNumberColumn: ColumnGroupColumnCell[] = [
            {
                columnKey: 'mobile',
                displayName: translations.phonenumber || 'Phone number',
                isSortable: true,
                size: 'column-flex',
                onClickColumn: sortByColumn
            }
        ];

        const phoneNumberRows: ColumnGroupRowCell[][] = contacts.map((contactRow) => ([
            {
                columnKey: 'mobile',
                id: `phone-${contactRow.id}`,
                rowId: contactRow.id,
                title: contactRow.mobile,
                displayValue: contactRow.mobile || '-'
            }
        ]));

        const phoneNumberColumnGroup = createColumnGroup('', phoneNumberColumn, phoneNumberRows);

        return phoneNumberColumnGroup;
    };

    const createOptionSetColumnGroup = (contacts: ContactViewModel[], optionSet: OptionSetViewModel) => {
        const optionSetColumn: ColumnGroupColumnCell[] = [
            {
                columnKey: `optionSet-${optionSet.id}`,
                displayName: optionSet.name,
                isSortable: true,
                size: 'column-flex',
                onClickColumn: sortByColumn
            }
        ];

        const optionSetRows: ColumnGroupRowCell[][] = contacts.map((contactRow) => {
            // Find the selected option for this option set
            const selectedOption = contactRow.selectedOptions && contactRow.selectedOptions.length > 0
                ? contactRow.selectedOptions.find(option => option.optionSetId === optionSet.id)
                : undefined;

            return [{
                columnKey: `optionSet-${optionSet.id}`,
                id: `optionSet-${optionSet.id}-${contactRow.id}`,
                rowId: contactRow.id,
                title: selectedOption?.value || "-",
                displayValue: selectedOption?.value || "-"
            }];
        });

        const optionSetColumnGroup = createColumnGroup('', optionSetColumn, optionSetRows);

        return optionSetColumnGroup;
    };

    const createActionColumnGroup = (contacts: ContactViewModel[]) => {
        const actionColumn: ColumnGroupColumnCell[] = [
            {
                columnKey: 'actions',
                displayName: '',
                isSortable: false,
                size: 'column-xsmall'
            }
        ];

        const actionRows: ColumnGroupRowCell[][] = contacts.map((contactRow) => ([
            {
                columnKey: 'actions',
                id: `actions-${contactRow.id}`,
                rowId: contactRow.id,
                title: '',
                displayValue: <TableCellActionDropDownButton
                    onClickRow={() => { }}
                    clickable={false}
                    displayText=''
                    listItems={generateDropDownListItems(contactRow)}
                    columnSize='column-small'
                    showDropDown = {(id) => setVisibleDropDownId(id)}
					dropDownId = {visibleDropDownId}
					rowId = {contactRow.id}
                />
            },
        ]));

        const actionColumnGroup = createColumnGroup('', actionColumn, actionRows);

        return actionColumnGroup;
    };

    const generateDropDownListItems = (contactRow: ContactViewModel) => [
        (
            <li key="edit" onClick={() => onClickEdit(contactRow.id)}>
                <div className="vertical-aligner">
                    <IconContext.Provider value={{ className: 'dropdown-icon' }}>
                        <RiPencilFill />
                    </IconContext.Provider>
                    <span>{translations.editcontact || 'Edit contact'}</span>
                </div>
            </li>
        ),
        (
            <li key="delete" onClick={() => onClickDelete(contactRow.id)}>
                <div className="vertical-aligner">
                    <IconContext.Provider value={{ className: 'dropdown-icon' }}>
                        <RiDeleteBin7Fill />
                    </IconContext.Provider>
                    <span>{translations ? translations.delete : 'Delete'}</span>
                </div>
            </li>
        )
    ];

    useEffect(() => {
        if (contacts) {
            const allColumnGroups = [];
            const contactsCopy = [...contacts];

            if (currentSortingColumn) {
                // Check if we're sorting by an option set column
                if (currentSortingColumn.startsWith('optionSet-')) {
                    // Extract the option set ID from the column key
                    const optionSetId = parseInt(currentSortingColumn.split('-')[1]);

                    // Custom sort function for option sets
                    contactsCopy.sort((a, b) => {
                        // Find the selected option values
                        const valueA = a.selectedOptions?.find(opt => opt.optionSetId === optionSetId)?.value || '';
                        const valueB = b.selectedOptions?.find(opt => opt.optionSetId === optionSetId)?.value || '';

                        // Compare the values
                        if (sortedAscending) {
                            return String(valueA).localeCompare(String(valueB));
                        } else {
                            return String(valueB).localeCompare(String(valueA));
                        }
                    });
                } else {
                    // Use the standard sort function for regular columns
                    contactsCopy.sort(sortByKey(currentSortingColumn, sortedAscending));
                }
            }

            const nameColumnGroup = createNameColumnGroup(contactsCopy);
            const emailAddressColumnGroup = createEmailAddressColumnGroup(contactsCopy);
            const phoneNumberColumnGroup = createPhoneNumberColumnGroup(contactsCopy);

            allColumnGroups.push(nameColumnGroup);
            allColumnGroups.push(emailAddressColumnGroup);
            allColumnGroups.push(phoneNumberColumnGroup);

            // Add option set columns if available
            if (optionSets && optionSets.length > 0) {
                optionSets.forEach(optionSet => {
                    const optionSetColumnGroup = createOptionSetColumnGroup(contactsCopy, optionSet);
                    allColumnGroups.push(optionSetColumnGroup);
                });
            }

            // Add action column at the end
            const actionColumnGroup = createActionColumnGroup(contactsCopy);
            allColumnGroups.push(actionColumnGroup);

            setColumnGroups(allColumnGroups);
        }
    }, [contacts, optionSets, translations, currentSortingColumn, sortedAscending, pageNum, visibleDropDownId]);

    return (
        <ColumnGroupTable
            columngroups={columnGroups}
            sortedByColumn={currentSortingColumn}
            isSortedAscending={sortedAscending}
            hidePageInputs={true}
            navigateHandler={navigateHandler}
            isFluidTable={true}
        />
    );
}